<template>
  <div class="goal-editor-container text-medium-1" :class="{ shadow: showShadowOnContainer }">
    <div class="goal-owner-title-container row card-gutter">
      <div class="owner-container col-auto">
        <multi-user-selector
          id="goal-owner-container"
          :user-ids="memberIds"
          :entity="goal"
          userShortSize="28"
        ></multi-user-selector>
      </div>
      <div class="col pl-0">
        <input
          id="goal-title"
          v-model="goal.title"
          type="text"
          class="form-control goal-title text-extra-large"
          :class="{ 'errored-field': errorMessages.title }"
          placeholder="What is your goal?"
          v-autofocus:preventScroll="!krId"
          autocomplete="off"
          v-no-capture
        />
        <template v-if="errorMessages.title">
          <div class="field-error-message" v-for="message in errorMessages.title" :key="message">
            {{ message }}
          </div>
        </template>
        <text-area
          v-model="goal.description"
          :rows="2"
          placeholder="Why is this goal important?"
          :textarea-class="
            errorMessages.description ? 'errored-field goal-description' : 'goal-description'
          "
        >
        </text-area>
        <template v-if="errorMessages.description">
          <div
            class="field-error-message"
            v-for="message in errorMessages.description"
            :key="message"
          >
            {{ message }}
          </div>
        </template>
      </div>
    </div>
    <div class="krs card-gutter">
      <template v-for="(kr, index) in goal.keyResults">
        <div :key="kr.key" class="kr row position-relative" v-if="!kr.isArchived">
          <div class="kr-icon-wrapper" :id="'kr-icon-' + (index + 1)"><i class="kr-icon"></i></div>
          <div class="kr-owner-container col-auto">
            <multi-user-selector :user-ids="memberIds" :entity="kr"></multi-user-selector>
          </div>
          <div
            class="col p-0 d-flex text-nowrap kr-meta"
            :class="{ 'errored-field': getKrErrors(index) }"
          >
            <input
              :ref="'kr-title-' + kr.id"
              v-model="kr.title"
              type="text"
              class="form-control kr-title flex-grow-1 text-medium-1"
              placeholder="Key result or milestone ('Increase sales by x%' or 'Deliver X by Y date')"
              v-autofocus="krId && (kr.id === Number(krId) || krId === -1)"
              v-no-capture
            />
            <div class="kr-options" :id="'kr-options-' + (index + 1)" tabindex="-1">
              <span class="kr-value" v-if="kr.measureBy === 'boolean'">
                Open <span class="text-secondary">/ Close</span>
              </span>
              <span class="kr-value" v-else-if="kr.measureBy === 'number'">
                {{ formatNumberValue(kr.start) }} - {{ formatNumberValue(kr.end) }}
              </span>
              <span class="kr-value" v-else-if="kr.measureBy === 'percentage'">
                {{ formatNumberValue(kr.start) }}% - {{ formatNumberValue(kr.end) }}%
              </span>
              <span class="kr-value" v-else-if="kr.measureBy === 'money'">
                ${{ formatNumberValue(kr.start) }} - ${{ formatNumberValue(kr.end) }}
              </span>
              <i class="ellipsis-horizontal-icon ml-2"></i>
            </div>
          </div>
          <b-popover
            :target="'kr-options-' + (index + 1)"
            placement="bottomleft"
            triggers="click blur"
          >
            <kr-values-editor :kr="kr" @change="updateKr(index, $event)" />
          </b-popover>

          <div class="kr-errors" v-if="getKrErrors(index)">
            <div class="field-error-message" v-for="error in getKrErrors(index)" :key="error">
              {{ error }}
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="row mb-3 card-gutter">
      <div class="col-auto init-spacer"></div>
      <div class="col">
        <link-button class="underline new-kr-button text-medium-1" @click="makeNewKR">
          New key result or milestone
        </link-button>
      </div>
    </div>
    <b-collapse
      class="goal-options card-gutter grey-bg"
      id="goal-options"
      v-model="expandGoalOptions"
    >
      <div class="row pb-2 goal-options-heading">
        <div class="col-auto init-spacer"></div>
        <div class="col d-flex justify-content-between">
          <span class="goal-options-expanded-button text-normal-1 bold pl-0">
            Goal options
          </span>
          <div class="d-flex">
            <button
              @click="openAITemplatesModal"
              class="btn bordered-button d-flex align-items-center text-medium"
              type="button"
              id="launch-goal-templates"
              v-if="isNewGoal && expandGoalOptions"
            >
              <i class="light-icon flex-shrink-0 mr-2"></i>
              {{ isAIFeatureEnabled ? 'AI Goal templates' : 'Goal Templates' }}
            </button>
            <button
              @click="expandGoalOptions = false"
              class="btn bordered-button ml-2 item text-medium"
              type="button"
              :class="{ 'd-none': !expandGoalOptions }"
            >
              <span class="mr-2">Less</span>
              <img src="@/assets/overflow.svg" />
            </button>
          </div>
        </div>
      </div>
      <div class="row field-row">
        <div class="col-auto init-spacer"></div>
        <div class="col-auto row flex-grow-1 field-wrapper align-items-center">
          <label for="timeline" class="col-4">
            Goal timeline:
            <br />
            When is the goal due?
          </label>
          <div class="col-auto d-flex align-items-center" id="timeline">
            <dropdown
              :selected="goal.timeline"
              :options="creationIntervals"
              :clearable="false"
              toggle-btn-class="bg-white"
              @change="goal.timeline = $event"
            />
            <timeline-mismatch-callout class="ml-2" v-if="isTimelineNotMatching" />
          </div>
          <template v-if="errorMessages.timeline">
            <div
              class="field-error-message"
              v-for="message in errorMessages.timeline"
              :key="message"
            >
              {{ message }}
            </div>
          </template>
        </div>
      </div>
      <div class="row field-row">
        <div class="col-auto init-spacer"></div>
        <div class="row flex-grow-1 field-wrapper align-items-center">
          <label for="checkin-frequency" class="col-4"
            >How often will teams check-in to update this goal?</label
          >
          <div class="col-auto" id="checkin-frequency">
            <dropdown
              :selected="goal.checkinFrequency"
              :options="checkinFrequencyOptions"
              :clearable="false"
              toggle-btn-class="bg-white"
              @change="goal.checkinFrequency = $event"
            />
          </div>
        </div>
      </div>
      <div class="row field-row flex-nowrap">
        <div class="col-auto init-spacer"></div>
        <div class="row flex-grow-1 field-wrapper align-items-center overflow-hidden">
          <label class="col-4">Which parent goals does this goal contribute to?</label>
          <div class="col-7">
            <edit-parent-goals
              class="pl-1"
              :parent-goals="parentGoals"
              :org-id="orgId"
              :sub-org-id="subOrgId"
              :goal="goal"
              :error-messages="errorMessages"
              :loading-state="parentGoalsFetchStatus"
              @remove="onParentGoalRemoval"
              @select="parentGoals = $event"
            />
          </div>
        </div>
      </div>
      <div class="row field-row">
        <div class="col-auto init-spacer"></div>
        <div class="row flex-grow-1 field-wrapper align-items-center" id="select-team">
          <label for="checkin-frequency" class="col-4"
            >Is this a team or an organization level goal?</label
          >
          <div class="col-auto">
            <div class="row">
              <div class="col-auto">
                <input id="goal-type-team" class="mr-2" type="radio" value="team" v-model="type" />
                <label for="goal-type-team">Team</label>
              </div>
              <div class="col-auto">
                <input
                  id="goal-type-organization"
                  class="mr-2"
                  type="radio"
                  value="organization"
                  v-model="type"
                />
                <label for="goal-type-organization">Org ({{ orgName }})</label>
              </div>
            </div>
            <dropdown
              class="mt-2"
              label="Choose a team"
              :selected="goal.teamId"
              :options="teams"
              :clearable="false"
              :disabled="type === 'organization'"
              toggle-btn-class="bg-white"
              @change="goal.teamId = $event"
            />
            <template v-if="errorMessages.team">
              <div class="field-error-message" v-for="message in errorMessages.team" :key="message">
                {{ message }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </b-collapse>
    <div class="card-gutter grey-bg error-wrapper" v-if="errorMessages.errors">
      <error-alert
        class="m-0"
        :showError="showFormError"
        @show="showFormError = $event"
        :errors="errorMessages.errors"
      />
    </div>
    <div class="row submit-row card-gutter grey-bg">
      <div class="col-auto">
        <div style="width: 24px;"></div>
      </div>
      <div class="col d-flex justify-content-between px-0 flex-wrap">
        <submit-buttons
          :submit-text="isNewGoal ? 'Add this goal' : 'Save'"
          :is-loading="isSubmitting"
          @save="updateGoal"
          @cancel="cancelGoalEdit"
        />
        <div class="collapsed-options d-flex" v-if="!expandGoalOptions">
          <button
            @click="openAITemplatesModal"
            class="btn bordered-button d-flex align-items-center text-medium"
            type="button"
            id="launch-goal-templates"
            v-if="isNewGoal && !expandGoalOptions"
          >
            <i class="light-icon flex-shrink-0 mr-2"></i>
            {{ isAIFeatureEnabled ? 'AI goal templates' : 'Goal templates' }}
          </button>
          <button
            @click="expandGoalOptions = true"
            class="btn bordered-button ml-2 text-medium"
            type="button"
          >
            <span class="mr-2">More</span>
            <img src="@/assets/overflow.svg" />
          </button>
        </div>
      </div>
    </div>
    <templates-modal
      v-model="templatesModalShown"
      :org-id="orgId"
      :team-id="teamId"
      @select="onTemplateSelection"
      v-if="isNewGoal"
    />
  </div>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton'
import Dropdown from '@/components/Dropdown'
import ErrorAlert from '@/components/ErrorAlert'
import TextArea from '@/components/form/TextArea'
import EditParentGoals from '@/components/goal/EditParentGoals'
import TimelineMismatchCallout from '@/components/goal/TimelineMismatchCallout'
import KrValuesEditor from '@/components/KrValuesEditor'
import SubmitButtons from '@/components/SubmitButtons'
import TemplatesModal from '@/components/templates/TemplatesModal'
import { LoadingState } from '@/constants/common'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import { TOAST_TYPE } from '@/constants/toasts'
import editManager from '@/editManager'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import TeamMixin from '@/mixins/TeamMixin'
import capitalize from '@/utils/capitalize'
import { getYourWorkPageRouteConfig } from '@/utils/navigationConfigs'
import showToast from '@/utils/showToast'
import { BCollapse, BPopover } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { formatNumber, newKRData } from '../../goalUtils'
import { creationIntervals, intervalWithId } from '../../interval'
import * as keymapService from '../../services/keymapService/keymapService'
import MultiUserSelector from '../MultiUserSelector'

const checkinFrequencyOptions = [
  { id: 'daily', value: 'Daily' },
  { id: 'weekly', value: 'Once a week' },
  { id: 'monthly', value: 'Once a month' }
]

export default {
  name: 'GoalEditor',
  mixins: [MembershipsMixin, TeamMixin, FeaturesMixin, EventBusMixin],
  components: {
    MultiUserSelector,
    BCollapse,
    ErrorAlert,
    KrValuesEditor,
    BPopover,
    SubmitButtons,
    EditParentGoals,
    Dropdown,
    LinkButton,
    TemplatesModal,
    TextArea,
    TimelineMismatchCallout
  },
  props: {
    orgId: [Number, String],
    goal: Object,
    krId: [Number, String],
    errorMessages: [Object, String],
    expanded: {
      type: Boolean,
      default: false
    },
    isSubmitting: Boolean,
    defaultParentGoals: {
      type: Array,
      default: () => []
    },
    showShadowOnContainer: Boolean,
    /**
     * Go to previous page on cancel
     */
    backOnCancel: Boolean
  },
  data() {
    const goalTeam = this.$store.getters.teamById(this.goal.teamId)
    return {
      expandGoalOptions: this.expanded,
      creationIntervals: creationIntervals().map(interval => ({
        ...interval,
        value: interval.name
      })),
      orgTypeGoals: [],
      teamTypeGoals: [],
      showFormError: false,
      parentGoalModalShown: false,
      templatesModalShown: false,
      type: goalTeam.isOrgLike ? 'organization' : 'team',
      isNewGoal: !this.goal.id,
      assignedParentGoals: [],
      selectedParentGoals: [],
      parentGoals: this.defaultParentGoals,
      childGoals: [],
      savedParentGoals: [],
      parentGoalsFetchStatus: LoadingState.LOADING,
      LoadingState,
      checkinFrequencyOptions,
      isAIFeatureEnabled: this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_AI)
    }
  },
  computed: {
    isTimelineNotMatching() {
      const goalTimeline = this.goal.timeline
      const isParentDisjointed = this.parentGoals.some(parent =>
        this.areDisjointedTimelines(parent.timeline, goalTimeline)
      )
      const isChildDisjointed = this.childGoals.some(child =>
        this.areDisjointedTimelines(child.timeline, goalTimeline)
      )
      return isParentDisjointed || isChildDisjointed
    },
    selectedInterval() {
      return intervalWithId(this.goal.timeline)
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    orgName() {
      const team = this.$store.getters.teamById(this.subOrgId)
      return team.name
    },
    teams() {
      return this.$store.getters
        .userTeamsExceptRootByOrgId(this.orgId)
        .filter(
          team =>
            !team.isArchived &&
            team.parentId === this.subOrgId &&
            !team.isOrgLike &&
            team.preferences.enableGoals
        )
        .map(team => ({ ...team, value: team.name }))
    },
    teamId() {
      return this.goal.teamId
    },
    user() {
      return this.$store.getters.user
    },
    memberships() {
      return this.$store.getters.teamMemberships(this.goal.teamId)
    },
    memberIds() {
      return this.memberships.map(membership => membership.user.id)
    },
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.teamId)
    }
  },
  methods: {
    ...mapActions(['getGoals']),
    areDisjointedTimelines(timeline1, timeline2) {
      let { start: t1Start, end: t1End } = intervalWithId(timeline1)
      let { start: t2Start, end: t2End } = intervalWithId(timeline2)
      t1Start = new Date(t1Start).getTime()
      t1End = new Date(t1End).getTime()
      t2Start = new Date(t2Start).getTime()
      t2End = new Date(t2End).getTime()
      let isOverlapping = false
      if ((t1Start >= t2Start && t1End <= t2End) || (t2Start >= t1Start && t2End <= t1End)) {
        isOverlapping = true
      }
      return !isOverlapping
    },
    formatNumberValue(value) {
      return formatNumber(value, undefined, 21)
    },
    makeNewKR() {
      this.goal.keyResults.push(newKRData(this.user.id))
      setTimeout(() => {
        let krEls = this.$el.querySelectorAll('.kr')
        let lastKrEl = krEls[krEls.length - 1]
        lastKrEl.querySelector('.kr-title').focus()
      }, 100)
    },
    handleError() {
      if (this.errorMessages.errors) {
        this.showFormError = true
      } else {
        this.showFormError = false
        const handledErrors = ['title', 'description', 'timeline', 'team']
        const unhandled = R.omit(handledErrors, this.errorMessages)
        if (typeof this.errorMessages === 'string') {
          showToast(this.errorMessages, TOAST_TYPE.ERROR)
        } else if (!R.isEmpty(unhandled)) {
          const messages = Object.values(unhandled)[0]
          showToast(messages.join('. '), TOAST_TYPE.ERROR)
        }
      }
    },
    cancelGoalEdit() {
      if (this.backOnCancel) {
        if (this.$store.state.visitedRoutes.length > 1) {
          window.history.back()
        } else {
          const location = getYourWorkPageRouteConfig({
            orgId: this.orgId,
            subOrgId: this.getClosestOrgLikeTeamId(this.teamId)
          })
          this.$router.push(location)
        }
      }
      this.$emit('cancel')
    },
    getKrErrors(index) {
      const emptyTitleCount = this.goal.keyResults.slice(0, index).filter(x => !x.title).length
      const actualIndex = index - emptyTitleCount
      // While sending keyResults to server, if ignore those key results that do not have title
      // actualIndex is the index at which current KR was sent to server
      const krErrors = R.pathOr({}, ['keyResults', actualIndex], this.errorMessages)
      if (R.isEmpty(krErrors)) {
        return null
      } else {
        const errors = R.mapObjIndexed((errors, key) => {
          return errors.map(error => capitalize(key) + ': ' + error)
        }, krErrors)
        return R.flatten(Object.values(errors))
      }
    },
    updateGoal() {
      const payload = {}
      if (this.hasParentGoalsChanged()) {
        payload.parentGoals = this.parentGoals
      }
      this.$emit('save-goal', payload)
    },
    updateKr(index, newKrInfo) {
      this.$set(this.goal.keyResults, index, newKrInfo)
    },
    async fetchParentGoals() {
      try {
        if (!this.isNewGoal) {
          this.savedParentGoals = this.parentGoals = await this.getGoals({
            orgId: this.orgId,
            options: {
              childId: this.goal.id
            }
          })
          this.parentGoalsFetchStatus = LoadingState.LOADED
        } else {
          this.parentGoalsFetchStatus = LoadingState.LOADED
        }
      } catch (e) {
        this.parentGoalsFetchStatus = LoadingState.ERRORED
        const message = R.pathOr(
          'Error while fetching parent goals',
          ['response', 'data', 'message'],
          e
        )
        this.errorMessages.parentGoals = [message]
      }
    },
    onParentGoalRemoval(goalId) {
      this.parentGoals = this.parentGoals.filter(goal => goal.id !== goalId)
    },
    hasParentGoalsChanged() {
      const savedParentsList = this.savedParentGoals.map(goal => goal.id).sort()
      const selectedParentsList = this.parentGoals.map(goal => goal.id).sort()
      return !R.equals(savedParentsList, selectedParentsList)
    },
    onTemplateSelection(goal) {
      this.$emit('select-template', goal)
    },
    async fetchChildGoals() {
      if (!this.isNewGoal) {
        this.childGoals = await this.getGoals({
          orgId: this.orgId,
          options: {
            parentId: this.goal.id
          }
        })
      }
    },
    openAITemplatesModal() {
      this.templatesModalShown = true
    }
  },
  mounted() {
    this.updateMemberships(this.orgId, this.goal.teamId)
    keymapService.activateCategory('edit-goal', {
      'add-kr': this.makeNewKR
    })
    this.listenToEventBus(events.SUBMIT_FORM, () => {
      if (!this.templatesModalShown && !this.parentGoalModalShown) {
        this.updateGoal()
      }
    })
    this.editId = editManager.enterEditMode()
    this.fetchParentGoals()
    this.fetchChildGoals()
  },
  beforeDestroy() {
    keymapService.deactivateCategory('edit-goal')
    editManager.exitEditMode(this.editId)
  },
  watch: {
    errorMessages: 'handleError',
    type(value) {
      if (value === 'organization') {
        this.goal.teamId = this.subOrgId
      }
    },
    teamId(teamId) {
      this.updateMemberships(this.orgId, teamId)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/forms';
@import '~@/styles/errors';

$grid-gutter-width: 6px;
$card-gutter: 25px;

.row {
  margin-left: -$grid-gutter-width;
  margin-right: -$grid-gutter-width;
}

.col,
.col-auto,
[class*='col-'] {
  padding-left: $grid-gutter-width;
  padding-right: $grid-gutter-width;
}

.init-spacer {
  width: 38px;
  flex-shrink: 0;
}

.goal-editor-container {
  border-radius: 16px;
  padding-top: $card-gutter;
  transform: translateY(-2px);
  background: white;
}

.shadow {
  box-shadow: $cardShadow;
}

.card-gutter {
  padding: 0 $card-gutter;
}

.goal-owner-title-container {
  .owner-container {
    padding-top: 2px;
  }
}

.goal-title {
  padding: 0 0 12px 0;
  font-weight: 600;
  height: unset;
}

/deep/ .goal-description {
  resize: none;
  padding: 0;
  border-radius: 0;
  border: 0;
  background: $documentColor;
}

.kr:first-of-type {
  margin-top: 8px;
}

.kr {
  margin-bottom: 10px;
  padding-right: 40px;
}

.kr-options {
  display: flex;
  align-items: center;
  min-width: 105px;
  cursor: pointer;
  border-radius: 20px;
  padding: 4px 10px;
  flex-shrink: 0;

  &[aria-describedby] {
    /* When popover is open*/
    background-color: $quinary;
  }

  &:focus {
    outline: none;
  }
}

.kr input {
  padding-left: 0;

  &:focus {
    border-bottom: 1px solid $brand;
  }
}

.errored-field input:focus {
  border-bottom: none;
}

.kr-meta {
  margin-left: 12px;
}

input,
/deep/ textarea {
  outline: none;
  box-shadow: none;
  border: 0;
}

input:focus,
/deep/ textarea:focus {
  outline: none;
  box-shadow: none;
  border: 0;
}

$placeholder-color: #adadad;

input::placeholder,
/deep/ textarea::placeholder {
  color: $placeholder-color;
}

/deep/ .dropdown-toggle {
  color: $primary;
}

.kr-measurement-separator {
  color: $placeholder-color;
}

.new-kr-button {
  padding-top: 0;
  padding-left: 0;
}

.user-photo-container {
  cursor: pointer;
}

.goal-options .custom-select {
  border-radius: $rounded-pill;
}

.form-control[type='text'] {
  border-radius: 0;
}

.owner-container {
  padding-right: 10px;
}

.kr-owner-container {
  padding-top: 4px;
  padding-right: 0;
}

.krs {
  padding-left: $card-gutter + 38px;
}

.error-wrapper {
  padding-top: $card-gutter;
}

.error-wrapper .alert {
  padding-left: 38px;
}

.kr-icon-wrapper {
  position: absolute;
  color: $secondary;
  font-size: 9px;
  left: -25px;
  top: 9px;
}

input {
  color: $primary;
}

.bordered-button {
  background: $quinary;
  border: 1px solid $borderPrimary;
  border-radius: 5px;
  padding: 4px 8px;
  color: $primary;

  &:hover {
    border-color: $tertiary;
  }
}

.submit-row {
  padding-top: $card-gutter;
  padding-bottom: $card-gutter;
  margin: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

.grey-bg {
  background-color: $quinary;
}

input[type='number'] {
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
}

.kr-errors {
  width: 100%;
  padding-left: 38px;
}

.boolean-value {
  position: absolute;
  left: 6px;
  top: 0;
  white-space: nowrap;
  color: $placeholder-color;
}

.field-wrapper {
  gap: 8.3%;
  padding-left: 0;
  margin-left: 6px;
}

.field-row {
  .field-wrapper {
    padding-bottom: 1rem;
    border-bottom: 1px solid $borderPrimary;
  }

  label {
    margin-bottom: 0;
    padding-left: 0;
  }

  &:not(:first-child) .field-wrapper {
    padding-top: 1rem;
  }
}

input[type='radio']:checked + label {
  font-weight: 600;
}

/deep/ .dd-field {
  min-width: 150px;
  min-height: 27px;
}

.goal-options-heading {
  padding-top: $card-gutter;
}
</style>
